import { type HTMLAttributes, forwardRef } from 'react';
import { cn } from '@/lib/utils';

const getUserFirstName = (userName: string): string => {
  return userName.split(' ')[0];
};
export interface HelloUserProps extends HTMLAttributes<HTMLHeadingElement> {
  /** Name to address the user by (e.g. "Daniel"). */
  userName?: string;
  /** Override the current hour (0–23). Useful for tests and Storybook. */
  hour?: number;
}

/**
 * Returns a time-of-day greeting based on a 24-hour clock.
 *
 * - 05:00–11:59 → "Good morning"
 * - 12:00–17:59 → "Good afternoon"
 * - 18:00–04:59 → "Good evening"
 */
function getGreeting(hour: number): string {
  if (hour >= 5 && hour < 12) return 'Good morning';
  if (hour >= 12 && hour < 18) return 'Good afternoon';
  return 'Good evening';
}

/**
 * Personalised greeting headline that adapts to the current time of day.
 *
 * @example
 * ```tsx
 * <HelloUser userName="Daniel" />
 * // → "Good morning, Daniel" (rendered between 05:00 and 11:59)
 * ```
 */
const HelloUser = forwardRef<HTMLHeadingElement, HelloUserProps>(
  ({ className, userName, hour = new Date().getHours(), ...props }, ref) => (
    <h1 ref={ref} className={cn('text-2xl font-semibold text-ink m-0 leading-normal', className)} {...props}>
      {getGreeting(hour)}, {getUserFirstName(userName ?? '')}
    </h1>
  ),
);
HelloUser.displayName = 'HelloUser';

export { HelloUser };
